import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Download, PieChart } from "lucide-react";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const ExpenseSummary = ({ summary, year, years, onYearChange }) => {
  const [exporting, setExporting] = useState(false);
  const max = Math.max(...(summary?.by_category || []).map((c) => c.amount), 1);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const { data } = await api.get(`/vault/export`, { params: year ? { year } : {}, responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = `scorppath-expenses-${year || "all"}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); } finally { setExporting(false); }
  };

  return (
    <div className="bg-[#114A31] text-white rounded-2xl p-6" data-testid="expense-summary">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2"><PieChart className="h-5 w-5 text-[#7FE3AE]" /><span className="font-heading font-semibold">Expense summary</span></div>
        <div className="flex gap-1 bg-white/10 rounded-full p-1" data-testid="vault-year-tabs">
          {[null, ...(years || [])].map((y) => (
            <button key={y ?? "all"} onClick={() => onYearChange(y)} data-testid={`vault-year-${y ?? "all"}`}
              className={`text-xs font-semibold px-3 py-1 rounded-full ${year === y ? "bg-white text-[#114A31]" : "text-white/70 hover:text-white"}`}>
              {y ?? "All"}
            </button>
          ))}
        </div>
      </div>
      <p className="mt-5 text-sm text-white/70">Total tracked{year ? ` in ${year}` : ""}</p>
      <p className="font-heading text-4xl font-bold" data-testid="vault-total">{money(summary?.total)}</p>
      <p className="text-xs text-white/60 mt-1">{summary?.count || 0} receipt{summary?.count === 1 ? "" : "s"}{summary?.needs_review ? ` · ${summary.needs_review} awaiting your review` : ""}</p>
      {summary?.mileage?.count > 0 && (
        <div className="mt-3 flex justify-between text-xs bg-white/10 rounded-xl px-3 py-2" data-testid="vault-mileage-line">
          <span className="text-white/80">+ Mileage · {summary.mileage.miles.toLocaleString()} mi</span>
          <span className="font-semibold">{money(summary.mileage.deduction)}</span>
        </div>
      )}
      {summary?.mileage?.count > 0 && (
        <div className="mt-1 flex justify-between text-sm px-3"><span className="text-white/80">Total deductible</span><span className="font-heading font-bold" data-testid="vault-grand-total">{money(summary.grand_total)}</span></div>
      )}

      {summary?.by_category?.length > 0 && (
        <ul className="mt-5 space-y-2" data-testid="vault-by-category">
          {summary.by_category.slice(0, 8).map((c) => (
            <li key={c.category}>
              <div className="flex justify-between text-xs"><span className="text-white/90">{c.category}</span><span className="text-white/70">{money(c.amount)}</span></div>
              <div className="mt-1 h-1.5 rounded-full bg-white/10"><div className="h-1.5 rounded-full bg-[#7FE3AE]" style={{ width: `${Math.max(3, (c.amount / max) * 100)}%` }} /></div>
            </li>
          ))}
        </ul>
      )}

      <Button onClick={exportCsv} disabled={exporting || !summary?.count} data-testid="vault-export-btn"
        className="mt-6 w-full rounded-full bg-white text-[#114A31] hover:bg-[#E6F0EB]">
        <Download className="h-4 w-4 mr-2" /> Export CSV{year ? ` (${year})` : ""}
      </Button>
    </div>
  );
};

export default ExpenseSummary;
